import { buildApiUrl } from '~/app/lib/api-config';
import type { SupervisorManualUploadResponse } from '~/app/landing/manual-upload/types';
import type { PipelineStepId } from '~/app/components/pipeline-debug/PipelineDebug/types';
import {
  readNdjsonStream,
  type PipelineStreamCallbacks,
} from '~/app/services/pipeline-debug/ndjson-stream';

export type { PipelineStreamCallbacks };

export async function uploadSupervisorManualLog(
  file: File,
): Promise<SupervisorManualUploadResponse> {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await fetch(
    buildApiUrl('/api/v1/supervisor/manual-log-upload'),
    {
      method: 'POST',
      credentials: 'include',
      body: formData,
    },
  );

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      message?: string | string[];
    } | null;

    const message = Array.isArray(payload?.message)
      ? payload.message.join('; ')
      : payload?.message;

    throw new Error(message ?? 'Manual upload failed.');
  }

  return (await response.json()) as SupervisorManualUploadResponse;
}

export async function clearSupervisorSandbox(): Promise<{
  clearedUploads: number;
}> {
  const response = await fetch(
    buildApiUrl('/api/v1/supervisor/manual-log-upload/sandbox'),
    {
      method: 'DELETE',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      message?: string | string[];
    } | null;

    const message = Array.isArray(payload?.message)
      ? payload.message.join('; ')
      : payload?.message;

    throw new Error(message ?? 'Failed to clear sandbox.');
  }

  return (await response.json()) as { clearedUploads: number };
}

export async function runSupervisorPipelineStepStream(
  stepId: PipelineStepId,
  input: unknown,
  callbacks: PipelineStreamCallbacks,
  signal?: AbortSignal,
): Promise<unknown> {
  const response = await fetch(
    buildApiUrl(
      `/api/v1/supervisor/manual-log-upload/steps/${stepId}/run-stream`,
    ),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ input }),
      signal,
    },
  );

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      message?: string | string[];
    } | null;

    const message = Array.isArray(payload?.message)
      ? payload.message.join('; ')
      : payload?.message;

    throw new Error(message ?? 'Pipeline step failed.');
  }

  if (!response.body) {
    throw new Error('Streaming is not supported in this browser.');
  }

  return readNdjsonStream(response.body, callbacks);
}
